import React, { useRef } from "react";
import {
  Box,
  Container,
  Typography,
  Paper,
  List,
  ListItem,
  Divider,
} from "@mui/material";
import { styled } from "@mui/material/styles";

// Styled components
const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  marginTop: theme.spacing(4),
  marginBottom: theme.spacing(4),
  borderRadius: theme.spacing(2),
  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  color: theme.palette.primary.main,
  fontWeight: 600,
  marginTop: theme.spacing(3),
  marginBottom: theme.spacing(2),
}));

const TnC = () => {
  // Create refs for each section
  const sectionRefs = {
    "OUR SERVICES": useRef(null),
    "INTELLECTUAL PROPERTY RIGHTS": useRef(null),
    "USER REPRESENTATIONS": useRef(null),
    "USER REGISTRATION": useRef(null),
    "PURCHASES AND PAYMENT": useRef(null),
    SUBSCRIPTIONS: useRef(null),
    "PROHIBITED ACTIVITIES": useRef(null),
  };

  // Handle click on table of contents item
  const handleSectionClick = (sectionName) => {
    sectionRefs[sectionName]?.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  return (
    <Container maxWidth="lg">
      <StyledPaper elevation={0}>
        <Typography variant="h3" gutterBottom align="center" color="primary">
          Terms and Conditions
        </Typography>
        <Typography
          variant="subtitle1"
          color="text.secondary"
          gutterBottom
          align="center"
        >
          Last updated December 28, 2024
        </Typography>
        <Divider sx={{ my: 3 }} />

        <Typography
          variant="h5"
          gutterBottom
          color="primary.main"
          sx={{ fontWeight: 600 }}
        >
          AGREEMENT TO OUR LEGAL TERMS
        </Typography>

        <Typography paragraph>
          We are Thesis Genius ('Company', 'we', 'us', or 'our'), a leading
          AI-powered academic writing platform based in India providing
          innovative research solutions.
        </Typography>

        <Typography paragraph>
          We operate the website thesisgen.com (the 'Site'), as well as any
          other related products and services that refer or link to these legal
          terms (the 'Legal Terms') (collectively, the 'Services').
        </Typography>

        <Typography paragraph>
          You can contact us by email at admin@thesisgen.com
        </Typography>

        <Typography paragraph>
          These Legal Terms constitute a legally binding agreement made between
          you, whether personally or on behalf of an entity ('you'), and Thesis
          Genius, concerning your access to and use of the Services. You agree
          that by accessing the Services, you have read, understood, and agreed
          to be bound by all of these Legal Terms. IF YOU DO NOT AGREE WITH ALL
          OF THESE LEGAL TERMS, THEN YOU ARE EXPRESSLY PROHIBITED FROM USING THE
          SERVICES AND YOU MUST DISCONTINUE USE IMMEDIATELY.
        </Typography>

        <Typography paragraph>
          Supplemental terms and conditions or documents that may be posted on
          the Services from time to time are hereby expressly incorporated
          herein by reference. We reserve the right, in our sole discretion, to
          make changes or modifications to these Legal Terms from time to time.
          We will alert you about any changes by updating the 'Last updated'
          date of these Legal Terms, and you waive any right to receive specific
          notice of each such change. It is your responsibility to periodically
          review these Legal Terms to stay informed of updates. You will be
          subject to, and will be deemed to have been made aware of and to have
          accepted, the changes in any revised Legal Terms by your continued use
          of the Services after the date such revised Legal Terms are posted.
        </Typography>

        <Typography paragraph>
          All users who are minors in the jurisdiction in which they reside
          (generally under the age of 18) must have the permission of, and be
          directly supervised by, their parent or guardian to use the Services.
          If you are a minor, you must have your parent or guardian read and
          agree to these Legal Terms prior to you using the Services.
        </Typography>

        <Typography paragraph>
          We recommend that you print a copy of these Legal Terms for your
          records.
        </Typography>

        <Box sx={{ my: 4 }}>
          <SectionTitle variant="h6">TABLE OF CONTENTS</SectionTitle>
          <List sx={{ bgcolor: "background.paper", borderRadius: 1 }}>
            {Object.keys(sectionRefs).map((item, index) => (
              <ListItem
                key={index}
                sx={{
                  cursor: "pointer",
                  "&:hover": {
                    bgcolor: "action.hover",
                    color: "primary.main",
                  },
                }}
                onClick={() => handleSectionClick(item)}
                component="button"
                role="link"
              >
                {`${index + 1}. ${item}`}
              </ListItem>
            ))}
          </List>
        </Box>

        {/* Content sections */}
        <Box sx={{ mt: 4 }} ref={sectionRefs["OUR SERVICES"]}>
          <SectionTitle variant="h6">1. OUR SERVICES</SectionTitle>
          <Typography paragraph>
            The information provided when using the Services is not intended for
            distribution to or use by any person or entity in any jurisdiction
            or country where such distribution or use would be contrary to law
            or regulation or which would subject us to any registration
            requirement within such jurisdiction or country. Accordingly, those
            persons who choose to access the Services from other locations do so
            on their own initiative and are solely responsible for compliance
            with local laws, if and to the extent local laws are applicable.
          </Typography>

          <Typography paragraph>
            The Services are not tailored to comply with industry-specific
            regulations (Health Insurance Portability and Accountability Act
            (HIPAA), Federal Information Security Management Act (FISMA), etc.),
            so if your interactions would be subjected to such laws, you may not
            use the Services. You may not use the Services in a way that would
            violate the Gramm-Leach-Bliley Act (GLBA).
          </Typography>
        </Box>

        <Box sx={{ mt: 4 }} ref={sectionRefs["INTELLECTUAL PROPERTY RIGHTS"]}>
          <SectionTitle variant="h6">
            2. INTELLECTUAL PROPERTY RIGHTS
          </SectionTitle>

          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 600, mt: 2, mb: 1 }}
          >
            Our intellectual property
          </Typography>
          <Typography paragraph>
            We are the owner or the licensee of all intellectual property rights
            in our Services, including all source code, databases,
            functionality, software, website designs, audio, video, text,
            photographs, and graphics in the Services (collectively, the
            'Content'), as well as the trademarks, service marks, and logos
            contained therein (the 'Marks').
          </Typography>

          <Typography paragraph>
            Our Content and Marks are protected by copyright and trademark laws
            (and various other intellectual property rights and unfair
            competition laws) and treaties in the United States and around the
            world.
          </Typography>

          <Typography paragraph>
            The Content and Marks are provided in or through the Services 'AS
            IS' for your personal, non-commercial use or internal business
            purpose only.
          </Typography>

          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 600, mt: 2, mb: 1 }}
          >
            Your use of our Services
          </Typography>
          <Typography paragraph>
            Subject to your compliance with these Legal Terms, including the
            'PROHIBITED ACTIVITIES' section below, we grant you a non-exclusive,
            non-transferable, revocable licence to:
          </Typography>
          <List>
            <ListItem>• access the Services; and</ListItem>
            <ListItem>
              • download or print a copy of any portion of the Content to which
              you have properly gained access,
            </ListItem>
          </List>
          <Typography paragraph>
            solely for your personal, non-commercial use or internal business
            purpose.
          </Typography>

          <Typography paragraph>
            Except as set out in this section or elsewhere in our Legal Terms,
            no part of the Services and no Content or Marks may be copied,
            reproduced, aggregated, republished, uploaded, posted, publicly
            displayed, encoded, translated, transmitted, distributed, sold,
            licensed, or otherwise exploited for any commercial purpose
            whatsoever, without our express prior written permission.
          </Typography>

          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 600, mt: 2, mb: 1 }}
          >
            Your submissions and contributions
          </Typography>
          <Typography paragraph>
            Please review this section and the 'PROHIBITED ACTIVITIES' section
            carefully prior to using our Services to understand the (a) rights
            you give us and (b) obligations you have when you post or upload any
            content through the Services.
          </Typography>

          <Typography sx={{ fontWeight: 600, mt: 1 }}>Submissions:</Typography>
          <Typography paragraph>
            By directly sending us any question, comment, suggestion, idea,
            feedback, or other information about the Services ('Submissions'),
            you agree to assign to us all intellectual property rights in such
            Submission. You agree that we shall own this Submission and be
            entitled to its unrestricted use and dissemination for any lawful
            purpose, commercial or otherwise, without acknowledgment or
            compensation to you.
          </Typography>
        </Box>

        <Box sx={{ mt: 4 }} ref={sectionRefs["USER REPRESENTATIONS"]}>
          <SectionTitle variant="h6">3. USER REPRESENTATIONS</SectionTitle>
          <Typography paragraph>
            By using the Services, you represent and warrant that: (1) all
            registration information you submit will be true, accurate, current,
            and complete; (2) you will maintain the accuracy of such information
            and promptly update such registration information as necessary; (3)
            you have the legal capacity and you agree to comply with these Legal
            Terms; (4) you are not a minor in the jurisdiction in which you
            reside, or if a minor, you have received parental permission to use
            the Services; (5) you will not access the Services through automated
            or non-human means, whether through a bot, script or otherwise; (6)
            you will not use the Services for any illegal or unauthorised
            purpose; and (7) your use of the Services will not violate any
            applicable law or regulation.
          </Typography>

          <Typography paragraph>
            If you provide any information that is untrue, inaccurate, not
            current, or incomplete, we have the right to suspend or terminate
            your account and refuse any and all current or future use of the
            Services (or any portion thereof).
          </Typography>
        </Box>

        <Box sx={{ mt: 4 }} ref={sectionRefs["USER REGISTRATION"]}>
          <SectionTitle variant="h6">4. USER REGISTRATION</SectionTitle>
          <Typography paragraph>
            You may be required to register to use the Services. You agree to
            keep your password confidential and will be responsible for all use
            of your account and password. We reserve the right to remove,
            reclaim, or change a username you select if we determine, in our
            sole discretion, that such username is inappropriate, obscene, or
            otherwise objectionable.
          </Typography>
        </Box>

        <Box sx={{ mt: 4 }} ref={sectionRefs["PURCHASES AND PAYMENT"]}>
          <SectionTitle variant="h6">5. PURCHASES AND PAYMENT</SectionTitle>
          <Typography paragraph>
            We accept the following forms of payment:
          </Typography>
          <List>
            {[
              "Visa",
              "Mastercard",
              "American Express",
              "Discover",
              "PayPal",
            ].map((payment, index) => (
              <ListItem key={index} sx={{ py: 0.5 }}>
                <Typography>• {payment}</Typography>
              </ListItem>
            ))}
          </List>

          <Typography paragraph sx={{ mt: 2 }}>
            You agree to provide current, complete, and accurate purchase and
            account information for all purchases made via the Services. You
            further agree to promptly update account and payment information,
            including email address, payment method, and payment card expiration
            date, so that we can complete your transactions and contact you as
            needed. Sales tax will be added to the price of purchases as deemed
            required by us. We may change prices at any time. All payments shall
            be in US dollars.
          </Typography>

          <Typography paragraph>
            You agree to pay all charges at the prices then in effect for your
            purchases and any applicable shipping fees, and you authorise us to
            charge your chosen payment provider for any such amounts upon
            placing your order. We reserve the right to correct any errors or
            mistakes in pricing, even if we have already requested or received
            payment.
          </Typography>

          <Typography paragraph>
            We reserve the right to refuse any order placed through the
            Services. We may, in our sole discretion, limit or cancel quantities
            purchased per person, per household, or per order. These
            restrictions may include orders placed by or under the same customer
            account, the same payment method, and/or orders that use the same
            billing or shipping address. We reserve the right to limit or
            prohibit orders that, in our sole judgement, appear to be placed by
            dealers, resellers, or distributors.
          </Typography>
        </Box>

        <Box sx={{ mt: 4 }} ref={sectionRefs["SUBSCRIPTIONS"]}>
          <SectionTitle variant="h6">6. SUBSCRIPTIONS</SectionTitle>

          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 600, mt: 2, mb: 1 }}
          >
            Billing and Renewal
          </Typography>
          <Typography paragraph>
            Your subscription will continue and automatically renew unless
            cancelled. You consent to our charging your payment method on a
            recurring basis without requiring your prior approval for each
            recurring charge, until such time as you cancel the applicable
            order. The length of your billing cycle will depend on the type of
            subscription plan you choose when you subscribed to the Services.
          </Typography>

          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 600, mt: 2, mb: 1 }}
          >
            Cancellation
          </Typography>
          <Typography paragraph>
            You can cancel your subscription at any time by contacting us using
            the contact information provided below. Your cancellation will take
            effect at the end of the current paid term. If you have any
            questions or are unsatisfied with our Services, please email us at
            admin@thesisgen.com.
          </Typography>

          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 600, mt: 2, mb: 1 }}
          >
            Fee Changes
          </Typography>
          <Typography paragraph>
            We may, from time to time, make changes to the subscription fee and
            will communicate any price changes to you in accordance with
            applicable law.
          </Typography>
        </Box>

        <Box sx={{ mt: 4 }} ref={sectionRefs["PROHIBITED ACTIVITIES"]}>
          <SectionTitle variant="h6">7. PROHIBITED ACTIVITIES</SectionTitle>
          <Typography paragraph>
            You may not access or use the Services for any purpose other than
            that for which we make the Services available. The Services may not
            be used in connection with any commercial endeavours except those
            that are specifically endorsed or approved by us.
          </Typography>

          <Typography paragraph>
            As a user of the Services, you agree not to:
          </Typography>
          <Typography paragraph>
            Systematically retrieve data or other content from the Services to
            create or compile, directly or indirectly, a collection,
            compilation, database, or directory without written permission from
            us.
          </Typography>
        </Box>

        <Box sx={{ mt: 4, p: 3, bgcolor: "primary.light", borderRadius: 2 }}>
          <Typography variant="body2" color="primary.contrastText">
            For any questions or concerns about these Terms and Conditions,
            please contact us at{" "}
            <Box component="span" sx={{ fontWeight: "bold" }}>
              admin@thesisgen.com
            </Box>
          </Typography>
        </Box>
      </StyledPaper>
    </Container>
  );
};

export default TnC;
